import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import { Button, Card, CardBody, CardTitle, Col, Input, Row } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  ICONS,
  ICON_KEYS,
  PRESETS,
  buildIframe,
  buildLink,
  buildSnippet,
  guessKind,
  iconLabel,
  presetOptions,
  previewStyle,
  readableContrast,
} from "./embedCode";

// Indirizzo pubblico del backend: serve a costruire link e codice da incollare nel sito del cliente.
export const publicUrl = (path) => {
  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/?$/, "/");
  return `${base.startsWith("http") ? base : window.location.origin + base}${path}`;
};

const copy = (text) =>
  (navigator.clipboard ? navigator.clipboard.writeText(text) : Promise.reject())
    .then(() => successToaster(i18n.t("cards:embed.copied")))
    .catch(() => ErrorToaster(i18n.t("cards:embed.copyFailed")));

const WHERE = ["inline", "right", "left", "center", "trigger", "page"];

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="d-block mb-1">{label}</label>
    {children}
  </div>
);

const CodeBox = ({ title, hint, code, rows = 3 }) => {
  const { t } = useTranslation("cards");
  return (
    <div className="mb-3">
      <label className="d-block mb-1">{title}</label>
      {hint && <p className="small text-muted mb-1">{hint}</p>}
      <Input type="textarea" readOnly rows={rows} value={code} onFocus={(e) => e.target.select()} />
      <Button size="sm" className="mt-1" onClick={() => copy(code)}>{t("embed.copy")}</Button>
    </div>
  );
};

// Generatore del bottone per il sito del ristorante: si sceglie l'aspetto e si copia il codice.
export const EmbedSnippets = ({ widgetPath, pagePath, hostId, label, kind }) => {
  const { t } = useTranslation("cards");
  const url = publicUrl(widgetPath);
  const pageUrl = publicUrl(pagePath || widgetPath.replace(/widget\.js$/, "page"));
  const [o, setO] = useState(() => {
    const start = presetOptions(kind || guessKind(label));
    return kind || !label ? start : { ...start, label };
  });
  const [kindKey, setKindKey] = useState(kind || guessKind(label));
  const set = (patch) => setO((prev) => ({ ...prev, ...patch }));
  const applyPreset = (key) => {
    setKindKey(key);
    set({ label: PRESETS[key].label, icon: PRESETS[key].icon });
  };

  const code = useMemo(() => buildSnippet(url, hostId, o), [url, hostId, o]);
  const link = useMemo(() => buildLink(pageUrl, o), [pageUrl, o]);
  const iframe = useMemo(() => buildIframe(pageUrl, o.label), [pageUrl, o.label]);
  const contrast = readableContrast(o);
  const isButton = o.where !== "trigger" && o.where !== "page";
  const iconPath = ICONS[o.icon];

  return (
    <>
      <p className="text-muted">
        {t("embed.intro")}
      </p>
      <Row>
        <Col lg="6">
          <Field label={t("embed.cardType")}>
            <div className="d-flex flex-wrap">
              {Object.entries(PRESETS).map(([key, p]) => (
                <Button key={key} size="sm" className="mr-1 mb-1" color={key === kindKey ? "primary" : "secondary"} onClick={() => applyPreset(key)}>
                  {p.name}
                </Button>
              ))}
            </div>
          </Field>
          <Field label={t("embed.where.title")}>
            <Input type="select" value={o.where} onChange={(e) => set({ where: e.target.value })}>
              {WHERE.map((v) => <option key={v} value={v}>{t(`embed.where.${v}`)}</option>)}
            </Input>
          </Field>
          {o.where === "trigger" && (
            <Field label={t("embed.trigger.label")}>
              <Input value={o.trigger} placeholder="#menu-vini, .apri-carta" onChange={(e) => set({ trigger: e.target.value })} />
              <p className="small text-muted mt-1 mb-0">
                {t("embed.trigger.help")}
              </p>
            </Field>
          )}
          {isButton && (
            <>
              <Field label={t("embed.buttonText")}>
                <Input value={o.label} maxLength={40} onChange={(e) => set({ label: e.target.value })} />
              </Field>
              <Row>
                <Col xs="6">
                  <Field label={o.style === "outline" ? t("embed.color") : t("embed.background")}>
                    <Input type="color" value={o.color} onChange={(e) => set({ color: e.target.value })} />
                  </Field>
                </Col>
                {o.style !== "outline" && (
                  <Col xs="6">
                    <Field label={t("embed.textColor")}>
                      <Input type="color" value={o.textColor} onChange={(e) => set({ textColor: e.target.value })} />
                    </Field>
                  </Col>
                )}
              </Row>
              {contrast !== null && contrast < 4.5 && (
                <p className="small text-warning">
                  {t("embed.lowContrast", { ratio: contrast.toFixed(1) })}
                </p>
              )}
              <Row>
                <Col xs="6">
                  <Field label={t("embed.icon")}>
                    <Input type="select" value={o.icon} onChange={(e) => set({ icon: e.target.value })}>
                      {ICON_KEYS.map((v) => <option key={v} value={v}>{iconLabel(v)}</option>)}
                    </Input>
                  </Field>
                </Col>
                <Col xs="6">
                  <Field label={t("embed.style")}>
                    <Input type="select" value={o.style} onChange={(e) => set({ style: e.target.value })}>
                      <option value="solid">{t("embed.styleSolid")}</option>
                      <option value="outline">{t("embed.styleOutline")}</option>
                    </Input>
                  </Field>
                </Col>
                <Col xs="6">
                  <Field label={t("embed.size")}>
                    <Input type="select" value={o.size} onChange={(e) => set({ size: e.target.value })}>
                      <option value="small">{t("embed.sizes.small")}</option>
                      <option value="medium">{t("embed.sizes.medium")}</option>
                      <option value="large">{t("embed.sizes.large")}</option>
                    </Input>
                  </Field>
                </Col>
                <Col xs="6">
                  <Field label={t("embed.corners")}>
                    <Input type="select" value={String(o.radius)} onChange={(e) => set({ radius: e.target.value })}>
                      <option value="0">{t("embed.radius.square")}</option>
                      <option value="8">{t("embed.radius.rounded")}</option>
                      <option value="pill">{t("embed.radius.pill")}</option>
                    </Input>
                  </Field>
                </Col>
              </Row>
              {o.where === "inline" && (
                <label className="d-block">
                  <input type="checkbox" checked={o.fullWidth} onChange={(e) => set({ fullWidth: e.target.checked })} /> {t("embed.fullWidth")}
                </label>
              )}
            </>
          )}
          {o.where !== "page" && (
            <Field label={t("embed.howOpens")}>
              <Input type="select" value={o.open} onChange={(e) => set({ open: e.target.value })}>
                <option value="modal">{t("embed.openModal")}</option>
                <option value="tab">{t("embed.openTab")}</option>
              </Input>
            </Field>
          )}
        </Col>
        <Col lg="6">
          <label className="d-block mb-1">{t("embed.preview")}</label>
          <div className="p-4 mb-3 text-center" style={{ background: "#f4f1ea", borderRadius: 8, minHeight: 90 }}>
            {isButton ? (
              <span style={previewStyle(o)}>
                {iconPath && (
                  <svg viewBox="0 0 24 24" width="1.15em" height="1.15em" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: ".5em", flex: "none" }}>
                    <path d={iconPath} />
                  </svg>
                )}
                {o.label || "…"}
              </span>
            ) : (
              <span className="text-muted small">
                {o.where === "trigger" ? t("embed.previewTrigger") : t("embed.previewPage")}
              </span>
            )}
            {isButton && o.where !== "inline" && (
              <p className="small text-muted mt-2 mb-0">{t(`embed.fixed.${o.where === "left" ? "left" : o.where === "center" ? "center" : "right"}`)}</p>
            )}
          </div>
          <CodeBox title={t("embed.codeTitle")} code={code} rows={4} />
          <CodeBox
            title={t("embed.linkTitle")}
            hint={t("embed.linkHint")}
            code={link}
            rows={3}
          />
        </Col>
      </Row>
      <details className="mt-2">
        <summary className="text-muted">{t("embed.webmaster.title")}</summary>
        <div className="mt-3">
          <ul className="small text-muted pl-3">
            <li>
              {t("embed.webmaster.platforms")}
            </li>
            <li>
              <Trans t={t} i18nKey="embed.webmaster.csp" values={{ origin: new URL(url).origin }} components={{ code: <code /> }} />
            </li>
            <li>
              {t("embed.webmaster.multiple")}
            </li>
            <li>
              <Trans
                t={t}
                i18nKey="embed.webmaster.stats"
                components={{
                  code: <code />,
                  example: <code>{'window.addEventListener("risto-widget:open", e => gtag("event", "apri_carta", {nome: e.detail.label}))'}</code>,
                }}
              />
            </li>
            <li>
              {t("embed.webmaster.link")}
            </li>
            <li>
              <Trans t={t} i18nKey="embed.webmaster.options" components={{ code: <code /> }} />
            </li>
          </ul>
          <CodeBox title={t("embed.webmaster.iframe")} code={iframe} rows={3} />
        </div>
      </details>
    </>
  );
};

// Scheda completa: link, QR e generatore del pulsante da incollare.
const EmbedCard = ({ title, pagePath, widgetPath, hostId, label, kind }) => {
  const link = publicUrl(pagePath);
  return (
    <Card>
      <CardBody>
        <CardTitle tag="h4">{title}</CardTitle>
        <Row className="align-items-center mb-3">
          <Col md="9"><Input readOnly value={link} onFocus={(e) => e.target.select()} /></Col>
          <Col md="3" className="text-center mt-3 mt-md-0"><QRCodeSVG value={link} size={110} /></Col>
        </Row>
        <EmbedSnippets widgetPath={widgetPath} pagePath={pagePath} hostId={hostId} label={label} kind={kind} />
      </CardBody>
    </Card>
  );
};

export default EmbedCard;
